import axios from "axios";
import { generateRandomString } from "./SharedFunctions";

const eventTypes = Object.freeze({
  LOGIN_ATTEMPT: "LoginAttempt",
  LOGIN: "Login",
  LOGOUT: "Logout",
  LOGIN_FAILED: "LoginFailed",
  NAVIGATION_START: "NavigationStart",
  NAVIGATION_END: "NavigationEnd",
  RESIZE: "Resize",
  API_REQUEST_START: "ApiRequestStart",
  API_REQUEST_END: "ApiRequestEnd",
  API_BAD_REQUEST_RESULT: "ApiBadRequestResult",
  GENERIC_ERROR: "GenericError",
  GLOBAL_ERROR: "GlobalError",
  EXTERNAL_SCRIPT_ERROR: "ExternalScriptError",
  UNHANDLED_PROMISE_REJECTION: "UnhandledPromiseRejection",
  APPLICATION_ERROR: "ApplicationError",
  APP_LOADED: "AppLoaded",
  PAGE_RELOADING: "PageReloading",
  PAGE_RELOADED: "PageReloaded"
});

const controller = new AbortController();
const connectionType = () =>
  // @ts-ignore
  navigator.connection
    ? // @ts-ignore
      navigator.connection.effectiveType
    : "Connection type not available";
const isProbablyMobile = () =>
  navigator.maxTouchPoints > 1 && window.innerWidth < 768;
const getWindowSize = () => {
  return { height: window.innerHeight, width: window.innerWidth };
};
const getTimeStamp = () => new Date().toLocaleString();

let events: any = [];
const getSessionId = () =>
  sessionStorage.getItem("maintreq-session") || "No session ID available";

function writePageLoadEvent() {
  const reload = sessionStorage.getItem("maintreq-reload");

  if (reload && reload === "true") {
    // There's an event listener for the beforeunload event that will add an item to sessionStorage
    // indicating the page was reloaded. sessionStoreage is preferred over localStorage because it
    // persists between refreshes/reloads and is wiped out automatically when the corresponding tab
    // or window is closed.
    events.push({
      type: eventTypes.PAGE_RELOADED
    });
    sessionStorage.removeItem("maintreq-reload");
    return;
  }

  const resourceList = window.performance.getEntriesByType("resource");
  const resources = JSON.parse(JSON.stringify(resourceList));
  const resourcesToSend: any = [];

  const sessionId = generateRandomString(12);
  sessionStorage.setItem("maintreq-session", sessionId);

  resources.forEach((r: any) => {
    resourcesToSend.push({
      name: r["name"],
      initiatorType: r["initiatorType"],
      connectStart: parseFloat(r["connectStart"]).toFixed(2),
      connectEnd: parseFloat(r["connectEnd"]).toFixed(2),
      duration: Math.round(r["duration"]),
      encodedBodySize: Math.round(r["encodedBodySize"]),
      decodedBodySize: Math.round(r["decodedBodySize"])
    });
  });

  const firstResource = resourceList[0];
  const lastResource = resourceList[resourceList.length - 1];
  const firstTime = firstResource.startTime;
  const lastTime = lastResource.startTime + lastResource.duration;
  const totalTime = lastTime - firstTime;

  events.push({
    type: eventTypes.APP_LOADED,
    timeStamp: getTimeStamp(),
    resources: resourcesToSend,
    loadTime: totalTime,
    windowSize: getWindowSize(),
    isProbablyMobile: isProbablyMobile(),
    userAgent: navigator.userAgent,
    connectionType: connectionType(),
    cookiesEnabled: navigator.cookieEnabled
  });
}

function writeWindowResizeEvent() {
  const event = {
    type: eventTypes.RESIZE,
    timeStamp: getTimeStamp(),
    newSize: getWindowSize()
  };

  events.push(event);
}

function writeLoginAttemptEvent(userName: string) {
  events.push({
    type: eventTypes.LOGIN_ATTEMPT,
    timeStamp: getTimeStamp(),
    userName: userName
  });
}

function writeLoginEvent(userName: string) {
  if (window.sessionStorage) {
    sessionStorage.setItem("maintreq-user", userName);
  }

  events.push({
    type: eventTypes.LOGIN,
    timeStamp: getTimeStamp(),
    userId: userName
  });
}

/**
 * @param {boolean} isLogoutExpired - Indicates the logout event is happening
 * because a user"s session expired.
 */
function writeLogoutEvent(isLogoutExpired: boolean) {
  events.push({
    type: eventTypes.LOGOUT,
    timeStamp: getTimeStamp(),
    session: sessionStorage.getItem("maintreq-user") || "Unable to get user ID",
    isAuthenticationExpired: isLogoutExpired
  });
}

function writeLoginFailedAttempt(userName: string, error: string) {
  events.push({
    type: eventTypes.LOGIN_FAILED,
    timeStamp: getTimeStamp(),
    userName: userName,
    error: error
  });
}

function writeNavStartEvent(
  currentRouteName: string,
  toRouteName: string,
  params: Record<string, unknown>,
  fullPath: string
) {
  events.push({
    type: eventTypes.NAVIGATION_START,
    timeStamp: getTimeStamp(),
    currentRoute: currentRouteName,
    to: toRouteName,
    params: params,
    path: fullPath
  });
}

/**
 * @param {string} fromName - previous route
 * @param {number} totalTime - RTT of the navigation
 * @param {boolean} redirect - indicates the route change is a result of being redirected
 */
function writeNavEndEvent(
  fromName: string,
  totalTime: number,
  redirect: string
) {
  events.push({
    type: eventTypes.NAVIGATION_END,
    timeStamp: getTimeStamp(),
    from: fromName,
    totalTime: totalTime,
    redirectedFrom: redirect || "Not recdirected"
  });
}

function writeApiRequestStartEvent(url: string, method: string, headers: Record<string, unknown>) {
  if (url.includes("events")) {
    return;
  }

  events.push({
    type: eventTypes.API_REQUEST_START,
    timeStamp: getTimeStamp(),
    url: url,
    method: method,
    headers: headers
  });
}

function writeApiRequestEndEvent(
  totalTime: number,
  status: string | number,
  url: string,
  errorMessage?: string
) {
  if (url.includes("events")) {
    return;
  }

  events.push({
    type: eventTypes.API_REQUEST_END,
    timeStamp: getTimeStamp(),
    totalTime: totalTime,
    url: url,
    statusCode: status,
    errorMessage: errorMessage !== null ? errorMessage : ""
  });
}

function writeApiBadRequestResultEvent(
  url: string,
  status: string | number,
  message: string,
  originalRequest: Record<string, unknown>
) {
  if (url.includes("events")) {
    return;
  }

  events.push({
    type: eventTypes.API_BAD_REQUEST_RESULT,
    timeStamp: getTimeStamp(),
    status: status,
    url: url,
    message: message || "No error message found in response",
    originalRequest: originalRequest
  });
}

function writeGlobalErrorEvent(
  message: string,
  source: string,
  lineno: number,
  colno: number,
  error: string
) {
  const msg = message.toLowerCase();
  const substr = "script error";

  if (msg.indexOf(substr) > -1) {
    // For when an error occurs in a script loaded from a different origin.
    events.push({
      type: eventTypes.EXTERNAL_SCRIPT_ERROR,
      timeStamp: getTimeStamp(),
      message: "External script error, gather logs from browser console."
    });
  } else {
    const err = {
      message: message,
      url: source,
      lineNumber: lineno,
      column: colno,
      error: JSON.stringify(error)
    };

    events.push({
      type: eventTypes.GLOBAL_ERROR,
      timeStamp: getTimeStamp(),
      error: err
    });
  }
}

function writeUhandledPromiseRejectionEvent(event: PromiseRejectionEvent) {
  events.push({
    type: eventTypes.UNHANDLED_PROMISE_REJECTION,
    timeStamp: getTimeStamp(),
    promise: event.promise,
    reason: event.reason
  });
}

function writeGenericErrorEvent(message: string, stack: string) {
  events.push({
    type: eventTypes.GENERIC_ERROR,
    timeStamp: getTimeStamp(),
    message: message,
    stackTrace: stack
  });
}

/**
 * Provides the ability to capture application-level errors. In the case of a Vue
 * app, this is useful for capturing data about errors at the component level.
 */
function writeApplicationErrorEvent(
  message: string,
  componentInfo: any,
  stack: string
) {
  events.push({
    type: eventTypes.APPLICATION_ERROR,
    timeStamp: getTimeStamp(),
    message: message,
    stackTrace: stack,
    component:
      componentInfo.$vnode.componentOptions.tag ||
      "Unable to determine component"
  });
}


function resetAnalytics() {
  controller.abort();
  sessionStorage.clear();
  events = [];
}

async function sendEvents() {
  if (events.length < 1) {
    return;
  }

  try {
    const reqBody = {
      userId: sessionStorage.getItem("maintreq-user") || "No user ID available",
      sessionId: getSessionId(),
      events: events
    };
    events = [];

    axios.post("/events", reqBody);
  } catch (err) {}
}


async function windowUnloading() {
  events.push({
    type: eventTypes.PAGE_RELOADING,
    timeStamp: getTimeStamp()
  });
  await sendEvents();
  sessionStorage.setItem("maintreq-reload", "true");  
}

const eventTracking = {
  timeStamp: () => new Date().toLocaleString(),
  calculateLoadTime: (start: number, end: number) => end - start,
  isCookiesEnabled: () => navigator.cookieEnabled,
  isProbablyMobile: () => isProbablyMobile(),
  userAgent: navigator.userAgent,
  connectionType: () => connectionType(),
  getWindowSize: () => {
    return { height: window.innerHeight, width: window.innerWidth };
  },
  writePageLoadMetrics: writePageLoadEvent,
  writeWindowResizeEvent: writeWindowResizeEvent,
  writeLoginAttemptEvent: (userName: string) =>
    writeLoginAttemptEvent(userName),
  writeLoginEvent: (userName: string) => writeLoginEvent(userName),
  writeLogoutEvent: (isExpired: boolean) => writeLogoutEvent(isExpired),
  writeLoginFailedEvent: (userName: string, err: string) =>
    writeLoginFailedAttempt(userName, err),
  writeNavStartEvent: (
    currentRoute: string,
    toRoute: string,
    params: Record<string, unknown>,
    path: string
  ) => writeNavStartEvent(currentRoute, toRoute, params, path),
  writeNavEndEvent: (from: string, time: number, redirect: string) =>
    writeNavEndEvent(from, time, redirect),
  writeApiRequestStartEvent: (url: string, method: string, headers: Record<string, unknown>) =>
    writeApiRequestStartEvent(url, method, headers),
  writeApiRequestEndEvent: (
    time: number,
    status: string | number,
    url: string,
    msg: string
  ) => writeApiRequestEndEvent(time, status, url, msg),
  writeApiBadRequestResultEvent: (
    url: string,
    status: string | number,
    msg: string,
    req: Record<string, unknown>
  ) => writeApiBadRequestResultEvent(url, status, msg, req),
  writeGlobalErrorEvent: (
    message: string,
    source: string,
    lineno: number,
    colno: number,
    error: string
  ) => writeGlobalErrorEvent(message, source, lineno, colno, error),
  writeUhandledPromiseRejectionEvent: (event: PromiseRejectionEvent) =>
    writeUhandledPromiseRejectionEvent(event),
  writeGenericErrorEvent: (msg: string, stack: string) =>
    writeGenericErrorEvent(msg, stack),
  writeApplicationErrorEvent: (
    message: string,
    componentInfo: any,
    stack: string
  ) => writeApplicationErrorEvent(message, componentInfo, stack),
  writePageUnloadingEvent: () => windowUnloading(),
  sendData: () => sendEvents(),
  stopSendingData: () => controller.abort(),
  resetAnalytics: () => resetAnalytics
};

export default eventTracking;
